import { useEffect, useRef, useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import {
  ChevronLeft, CheckCircle2, Loader2, AlertTriangle,
  PenLine, ShieldCheck,
} from 'lucide-react'
import { supabase } from '@/config/supabase'
import { ROUTES } from '@/config/routes'
import useAuthStore from '@/stores/authStore'
import Button from '@/components/ui/Button'

// ── Company details ───────────────────────────────────────────────────────────
const SK_SEC_REG = '2025120229***-00'
const SK_BIR_REG = '010-950-***-000'

function fmt(n) {
  if (n == null || isNaN(n)) return '—'
  return '₱' + Number(n).toLocaleString('en-PH', { minimumFractionDigits: 0 })
}

function fmtDate(iso) {
  return new Date(iso ?? Date.now()).toLocaleDateString('en-PH', {
    year: 'numeric', month: 'long', day: 'numeric',
  })
}

// ── Contract text component ───────────────────────────────────────────────────
function ContractBody({ proposal, quoteRef, profile, signedAt }) {
  const name    = profile?.full_name ?? '—'
  const city    = profile?.city ?? profile?.address?.split(',').pop()?.trim() ?? '—'
  const email   = profile?.email ?? '—'
  const dateStr = fmtDate(signedAt ?? new Date())

  function paymentTermsText() {
    if (!proposal) return '—'
    const total     = proposal.total_amount
    const pct       = proposal.downpayment_pct ?? 50
    if (proposal.payment_type === 'full') {
      return `Full payment of ${fmt(total)} upon contract signing.`
    }
    if (proposal.payment_type === 'downpayment') {
      const down = total != null ? total * (pct / 100) : null
      const bal  = total != null ? total * ((100 - pct) / 100) : null
      return (
        `Downpayment of ${fmt(down)} (${pct}%) upon contract signing.\n` +
        `Balance of ${fmt(bal)} due upon completion and sign-off.`
      )
    }
    return '—'
  }

  return (
    <div className="text-sm text-ink-primary leading-relaxed space-y-4 font-mono">

      <div className="text-center space-y-1">
        <p className="font-bold text-base">CONSTRUCTION SERVICES AGREEMENT</p>
        <p className="font-semibold">Siglat Konstract OPC</p>
      </div>

      <div className="border-t border-surface-border pt-3 space-y-0.5">
        <p><span className="text-ink-muted">Reference No:</span> {quoteRef ?? '—'}</p>
        <p><span className="text-ink-muted">Date:</span> {dateStr}</p>
      </div>

      <div className="space-y-3">
        <p className="font-semibold uppercase tracking-wide text-xs text-ink-muted">Parties</p>

        <div className="space-y-0.5">
          <p className="font-semibold">Client:</p>
          <p>Name: {name}</p>
          <p>Address: {city}</p>
          <p>Email: {email}</p>
        </div>

        <div className="space-y-0.5">
          <p className="font-semibold">Service Provider:</p>
          <p>Siglat Konstract OPC</p>
          <p>Civil Engineer &amp; Master Plumber</p>
          <p>SEC Registration No: {SK_SEC_REG}</p>
          <p>BIR Registration No: {SK_BIR_REG}</p>
        </div>
      </div>

      <div className="space-y-2">
        <p className="font-semibold uppercase tracking-wide text-xs text-ink-muted">Scope of Work</p>
        <p>
          All work shall be executed strictly based on the approved Bill of Quantities (BOQ)
          as stated in Final Proposal Reference {quoteRef ?? '—'}. Any additional work not
          in the BOQ requires written approval from both parties before execution.
        </p>
      </div>

      <div className="space-y-2">
        <p className="font-semibold uppercase tracking-wide text-xs text-ink-muted">Payment Terms</p>
        {paymentTermsText().split('\n').map((line, i) => (
          <p key={i}>{line}</p>
        ))}
      </div>

      <div className="space-y-2">
        <p className="font-semibold uppercase tracking-wide text-xs text-ink-muted">Client Obligations</p>
        <ol className="list-decimal list-inside space-y-1">
          <li>Pay Siglat Konstract per agreed terms.</li>
          <li>Provide site access during agreed working hours.</li>
          <li>Secure necessary permits from building administration if required.</li>
          <li>Sign off on completed work within 24 hours of notification.</li>
        </ol>
      </div>

      <div className="space-y-2">
        <p className="font-semibold uppercase tracking-wide text-xs text-ink-muted">Siglat Konstract Obligations</p>
        <ol className="list-decimal list-inside space-y-1">
          <li>Execute all work described in the approved BOQ.</li>
          <li>Use qualified and skilled workers.</li>
          <li>Complete work within estimated duration.</li>
          <li>Provide 30-day warranty on completed work.</li>
          <li>Maintain cleanliness and safety on site.</li>
          <li>Supply all materials — client-supplied materials not accepted to protect warranty coverage.</li>
        </ol>
      </div>

      <div className="space-y-2">
        <p className="font-semibold uppercase tracking-wide text-xs text-ink-muted">Warranty</p>
        <p>
          30 days from completion covering defects in workmanship. Does not cover
          damage caused by the client or third parties.
        </p>
      </div>

      <div className="space-y-2">
        <p className="font-semibold uppercase tracking-wide text-xs text-ink-muted">Materials Policy</p>
        <p>
          All materials supplied by Siglat Konstract only. Client-supplied materials
          void the 30-day warranty.
        </p>
      </div>

      <div className="space-y-2">
        <p className="font-semibold uppercase tracking-wide text-xs text-ink-muted">Dispute Resolution</p>
        <p>
          Disputes settled amicably first. If unresolved, subject to Philippine laws
          and jurisdiction of Las Piñas City courts.
        </p>
      </div>

      <div className="space-y-1 text-xs text-ink-muted border-t border-surface-border pt-3">
        <p>This agreement is executed electronically pursuant to Republic Act No. 8792</p>
        <p>(Electronic Commerce Act of the Philippines).</p>
      </div>

    </div>
  )
}

// ── Main page ─────────────────────────────────────────────────────────────────
export default function SignContract() {
  const { proposalId } = useParams()
  const navigate = useNavigate()
  const profile  = useAuthStore((s) => s.profile)

  const [loading,   setLoading]   = useState(true)
  const [proposal,  setProposal]  = useState(null)
  const [quoteRef,  setQuoteRef]  = useState('')
  const [pageError, setPageError] = useState(null)

  // Already signed?
  const [alreadySigned, setAlreadySigned] = useState(false)

  // Signature form
  const [nameInput,  setNameInput]  = useState('')
  const [nameError,  setNameError]  = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [done,       setDone]       = useState(false)

  // Scroll tracking — require user to reach bottom of contract
  const contractRef = useRef(null)
  const [hasScrolled, setHasScrolled] = useState(false)

  // ── Load proposal ────────────────────────────────────────────────────────
  useEffect(() => {
    if (!proposalId || !profile?.id) return

    async function load() {
      setLoading(true)

      // Load proposal + quote reference
      const { data: fp, error: fpErr } = await supabase
        .from('final_proposals')
        .select('id, booking_id, quote_id, status, payment_type, total_amount, downpayment_pct')
        .eq('id', proposalId)
        .single()

      if (fpErr || !fp) {
        setPageError('Proposal not found. Please check your projects.')
        setLoading(false)
        return
      }

      setProposal(fp)

      // Fetch quote reference
      if (fp.quote_id) {
        const { data: q } = await supabase
          .from('quotes')
          .select('quote_reference')
          .eq('id', fp.quote_id)
          .single()
        if (q?.quote_reference) setQuoteRef(q.quote_reference)
      }

      // Check if already signed by this customer
      const { data: existing } = await supabase
        .from('contracts')
        .select('id, status')
        .eq('proposal_id', proposalId)
        .eq('customer_id', profile.id)
        .maybeSingle()

      if (existing) setAlreadySigned(true)

      setLoading(false)
    }

    load()
  }, [proposalId, profile?.id]) // eslint-disable-line

  // ── Scroll detection ─────────────────────────────────────────────────────
  function handleContractScroll() {
    const el = contractRef.current
    if (!el) return
    const atBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 60
    if (atBottom) setHasScrolled(true)
  }

  // ── Submit signature ─────────────────────────────────────────────────────
  async function handleSign() {
    const profileName = profile?.full_name?.trim() ?? ''
    const typed       = nameInput.trim()

    if (!typed) {
      setNameError('Please type your full name to sign.')
      return
    }
    if (typed.toLowerCase() !== profileName.toLowerCase()) {
      setNameError(`Name must match your account name exactly: "${profileName}"`)
      return
    }
    setNameError('')
    setSubmitting(true)

    try {
      // Best-effort IP capture
      let ipAddress = null
      try {
        const res = await fetch('https://api.ipify.org?format=json', { signal: AbortSignal.timeout(3000) })
        const json = await res.json()
        ipAddress = json.ip ?? null
      } catch (_) { /* non-critical */ }

      const deviceInfo = navigator.userAgent?.slice(0, 500) ?? null
      const signedAt   = new Date().toISOString()

      const { error: insertErr } = await supabase
        .from('contracts')
        .insert({
          proposal_id:          proposalId,
          customer_id:          profile.id,
          quote_reference:      quoteRef || null,
          customer_name_signed: typed,
          customer_signed_at:   signedAt,
          customer_ip_address:  ipAddress,
          customer_device_info: deviceInfo,
          status:               'pending_sk',
        })

      if (insertErr) throw insertErr
      supabase.functions.invoke('send-notification', { body: {
        type: 'admin_contract_signed', customer_id: profile.id,
        data: { ref_number: quoteRef ?? '' },
      }}).catch(console.error)
      setDone(true)
    } catch (err) {
      console.error('Sign contract error:', err)
      setNameError(`Something went wrong: ${err?.message ?? 'Please try again.'}`)
    } finally {
      setSubmitting(false)
    }

    // Fire-and-forget: mark proposal as accepted (best-effort, admin can also do this)
    supabase
      .from('final_proposals')
      .update({ status: 'accepted', accepted_at: new Date().toISOString() })
      .eq('id', proposalId)
      .then(() => {}) // ignore result — customer may lack UPDATE policy
  }

  // ── Loading / error ──────────────────────────────────────────────────────
  if (loading) {
    return (
      <div className="min-h-dvh flex items-center justify-center bg-surface-base">
        <Loader2 className="w-6 h-6 text-siglat-teal animate-spin" />
      </div>
    )
  }

  if (pageError) {
    return (
      <div className="min-h-dvh flex flex-col items-center justify-center bg-surface-base gap-4 px-6 text-center">
        <AlertTriangle className="w-8 h-8 text-siglat-red" />
        <p className="text-ink-primary font-semibold">{pageError}</p>
        <button onClick={() => navigate(ROUTES.BOOK_HISTORY)} className="text-sm text-siglat-teal hover:underline">
          Back to My Projects
        </button>
      </div>
    )
  }

  // ── Success screen ───────────────────────────────────────────────────────
  if (done) {
    return (
      <div className="min-h-dvh flex flex-col bg-surface-base">
        <div className="bg-surface-raised border-b border-surface-border px-4 pt-12 pb-4 safe-top">
          <div className="page-container">
            <p className="text-sm text-ink-muted">Contract</p>
          </div>
        </div>
        <div className="flex-1 flex flex-col items-center justify-center px-6 text-center gap-5">
          <div className="w-16 h-16 rounded-full bg-siglat-teal/10 border border-siglat-teal/25 flex items-center justify-center">
            <ShieldCheck className="w-8 h-8 text-siglat-teal" />
          </div>
          <div>
            <p className="font-display text-xl font-bold text-ink-primary mb-2">
              Contract Signed
            </p>
            <p className="text-sm text-ink-muted leading-relaxed">
              Your signature has been recorded. Siglat Konstract will countersign
              and you will be notified once the contract is fully executed.
              You may then proceed with payment.
            </p>
          </div>
          <div className="bg-surface-raised border border-surface-border rounded-xl px-4 py-3 w-full max-w-xs text-sm text-ink-muted text-left space-y-1">
            <p>Signed as: <span className="text-ink-primary font-medium">{nameInput.trim()}</span></p>
            <p>Reference: <span className="text-ink-primary font-medium">{quoteRef || '—'}</span></p>
            <p>Date: <span className="text-ink-primary font-medium">{fmtDate(new Date())}</span></p>
          </div>
          <Button fullWidth size="lg" onClick={() => navigate(ROUTES.BOOK_HISTORY)}>
            Back to My Projects
          </Button>
        </div>
      </div>
    )
  }

  // ── Already signed ───────────────────────────────────────────────────────
  if (alreadySigned) {
    return (
      <div className="min-h-dvh flex flex-col bg-surface-base">
        <div className="bg-surface-raised border-b border-surface-border px-4 pt-12 pb-4 safe-top">
          <div className="page-container">
            <button
              onClick={() => navigate(ROUTES.BOOK_HISTORY)}
              className="flex items-center gap-1 text-sm text-ink-muted hover:text-ink-secondary mb-3 -ml-1"
            >
              <ChevronLeft className="w-4 h-4" /> My Projects
            </button>
            <p className="font-display text-xl font-bold text-ink-primary">Contract</p>
          </div>
        </div>
        <div className="flex-1 flex flex-col items-center justify-center px-6 text-center gap-4">
          <CheckCircle2 className="w-10 h-10 text-siglat-teal" />
          <div>
            <p className="font-display text-lg font-bold text-ink-primary mb-1">Already Signed</p>
            <p className="text-sm text-ink-muted">
              You have already signed this contract. Siglat Konstract is reviewing and will countersign shortly.
            </p>
          </div>
          <Button size="md" variant="secondary" onClick={() => navigate(ROUTES.BOOK_HISTORY)}>
            Back to My Projects
          </Button>
        </div>
      </div>
    )
  }

  // ── Main: contract + signature form ─────────────────────────────────────
  return (
    <div className="min-h-dvh bg-surface-base flex flex-col">

      {/* Header */}
      <div className="bg-surface-raised border-b border-surface-border px-4 pt-12 pb-4 safe-top shrink-0">
        <div className="page-container">
          <button
            onClick={() => navigate(ROUTES.BOOK_HISTORY)}
            className="flex items-center gap-1 text-sm text-ink-muted hover:text-ink-secondary mb-3 -ml-1"
          >
            <ChevronLeft className="w-4 h-4" /> My Projects
          </button>
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-siglat-teal/10 border border-siglat-teal/25 flex items-center justify-center shrink-0">
              <PenLine className="w-5 h-5 text-siglat-teal" />
            </div>
            <div>
              <h1 className="font-display text-xl font-bold text-ink-primary leading-tight">
                Review &amp; Sign Contract
              </h1>
              <p className="text-xs text-ink-muted">{quoteRef}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Body */}
      <div className="flex-1 overflow-y-auto">
        <div className="page-container py-5 flex flex-col gap-5">

          {/* Instruction banner */}
          <div className="bg-siglat-teal/10 border border-siglat-teal/30 rounded-xl px-4 py-3">
            <p className="text-sm text-siglat-teal font-medium">
              Read the full contract below, then type your name to sign.
            </p>
            <p className="text-xs text-siglat-teal/70 mt-0.5">
              Scroll to the bottom to unlock the signature field.
            </p>
          </div>

          {/* Contract body — scrollable box */}
          <div
            ref={contractRef}
            onScroll={handleContractScroll}
            className="bg-surface-raised border border-surface-border rounded-xl px-4 py-5 overflow-y-auto max-h-[55vh]"
          >
            <ContractBody
              proposal={proposal}
              quoteRef={quoteRef}
              profile={profile}
            />
          </div>

          {/* Scroll nudge */}
          {!hasScrolled && (
            <p className="text-xs text-ink-muted text-center -mt-2">
              ↓ Scroll to the bottom of the contract to proceed
            </p>
          )}

          {/* Signature section */}
          <div className={`flex flex-col gap-3 transition-opacity duration-300 ${hasScrolled ? 'opacity-100' : 'opacity-30 pointer-events-none'}`}>
            <div className="border-t border-surface-border pt-4">
              <p className="text-xs text-ink-muted uppercase tracking-widest mb-3">Client E-Signature</p>
              <p className="text-sm text-ink-secondary leading-relaxed mb-3">
                By typing your full name below and clicking <strong>"I Agree and Sign"</strong>, you confirm you have read, understood, and agree to all terms above.
              </p>
              <p className="text-xs text-ink-muted mb-2">
                Type exactly: <span className="text-ink-primary font-semibold">{profile?.full_name}</span>
              </p>

              <input
                type="text"
                value={nameInput}
                onChange={(e) => {
                  setNameInput(e.target.value)
                  setNameError('')
                }}
                placeholder={profile?.full_name ?? 'Your full name'}
                autoComplete="off"
                className={`w-full bg-surface-overlay border rounded-xl px-4 py-3.5 text-ink-primary placeholder-ink-muted text-base focus:outline-none focus:border-siglat-teal transition-colors
                  ${nameError ? 'border-siglat-red' : 'border-surface-border'}`}
              />
              {nameError && (
                <p className="text-xs text-siglat-red mt-1.5">⚠ {nameError}</p>
              )}
            </div>

            {/* Legal note */}
            <div className="bg-surface-overlay border border-surface-border rounded-xl px-4 py-3">
              <p className="text-xs text-ink-muted leading-relaxed">
                This electronic signature is legally binding under Republic Act No. 8792 (Electronic Commerce Act of the Philippines). Your signature timestamp and device information are recorded for verification.
              </p>
            </div>
          </div>

        </div>
      </div>

      {/* Bottom CTA */}
      <div className="shrink-0 bg-surface-raised border-t border-surface-border px-4 py-4 safe-bottom">
        <div className="page-container">
          <Button
            fullWidth
            size="lg"
            loading={submitting}
            onClick={hasScrolled ? handleSign : undefined}
            disabled={!hasScrolled}
          >
            I Agree and Sign This Contract
          </Button>
        </div>
      </div>

    </div>
  )
}
